import { useState, useRef, useCallback, useEffect } from "react";
import { useConfirm } from "@/components/app/ConfirmDialog";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { X, Send, Trash2, Loader2, MousePointer2, Square, ChevronLeft, ChevronRight, History, ArrowLeft, Download, RefreshCw, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ImageActionMenu } from "@/components/app/ImageActionMenu";
import { useVideoVersions } from "@/hooks/useVideoVersions";
import { FALLBACK_COSTS, useGenerationCosts } from "@/hooks/useGenerationCosts";
import { VideoToolbarControls, VideoPlayerOverlay, VideoPromptDialog } from "@/components/app/VideoControls";
import { cn } from "@/lib/utils";
import { downloadUrlAsFile } from "@/lib/download";
import { deleteVisualsAndAssets, countDeliverables } from "@/lib/deleteVisuals";
import { createStudioSession } from "@/hooks/useStudioSession";
import { FormatVariantsPanel } from "@/components/app/DeliverablesPanel";
import { motion, AnimatePresence } from "framer-motion";
import { supabase, getAuthHeader } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";


export interface Annotation {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  comment: string;
}

/**
 * Route prefixes Back is allowed to navigate to. A `from`/`returnTo` value can
 * be stale or hand-edited, and an unknown path lands on the catch-all 404 —
 * which is exactly how "/app/assets" used to strand people. Anything not
 * matching here falls through to history.
 *
 * Keep in sync with the /app children in App.tsx. `edit-visual` is omitted on
 * purpose: returning to this same page is never a useful Back.
 */
const KNOWN_APP_ROUTES = [
  "/app/products",
  "/app/studio",
  "/app/asset-library",
  "/app/settings",
];

const PRESET_ISSUES = [
  { key: "product_incorrect", label: "Product doesn't look correct", hint: "Re-attach product reference images so the AI can match the real product appearance" },
  { key: "wrong_placement", label: "Wrong placement / position", hint: "Product is placed incorrectly in the scene" },
  { key: "wrong_scale", label: "Wrong scale / proportions", hint: "Product size doesn't match reality" },
  { key: "wrong_color", label: "Wrong color / material", hint: "Product color or material finish is inaccurate" },
  { key: "lighting_mismatch", label: "Lighting doesn't match scene", hint: "Product lighting is inconsistent with the environment" },
  { key: "warm_airflow", label: "Add warm airflow glow from the unit's air outlet, a soft golden radiance spreading outward suggesting heat", hint: "Adds a visible warm air effect from the HVAC unit" },
] as const;

export type PresetIssueKey = (typeof PRESET_ISSUES)[number]["key"];

interface SiblingVisual {
  imageUrl: string;
  allVersionUrls: string[];
  renderId?: string;
  parentRenderId?: string;
  rootRenderId?: string;
}

interface EditRenderState {
  imageUrl: string;
  productImageUrls?: string[];
  allVersionUrls?: string[];
  sessionName?: string;
  renderId?: string;
  parentRenderId?: string;
  rootRenderId?: string;
  briefId?: string;
  returnTo: string;
  productInfo?: { name: string; brand?: string; category_key?: string; mounting_type?: string };
  siblingVisuals?: SiblingVisual[];
  currentSiblingIndex?: number;
}

export default function EditRenderPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const location = useLocation();
  const { user } = useAuth();
  const { workspace } = useWorkspace();

  const state = location.state as EditRenderState | undefined;

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [currentRect, setCurrentRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState(true);
  const imgContainerRef = useRef<HTMLDivElement>(null);

  const [selectedIssues, setSelectedIssues] = useState<Set<PresetIssueKey>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Per-version master flags. Keyed by visual id so the toggle applies to the
  // currently displayed version, not the visual that originally opened the page.
  const [versionMasters, setVersionMasters] = useState<Record<string, boolean>>({});
  // Keyboard shortcuts: Escape to delete annotation, Arrow keys to navigate siblings
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedId) {
        deleteAnnotation(selectedId);
        return;
      }
      // Arrow key sibling navigation
      if (isProcessing || !state?.siblingVisuals || state?.siblingVisuals.length <= 1) return;
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
      const idx = state?.currentSiblingIndex ?? 0;
      let nextIdx: number | null = null;
      if (e.key === "ArrowLeft" && idx > 0) nextIdx = idx - 1;
      if (e.key === "ArrowRight" && idx < state?.siblingVisuals.length - 1) nextIdx = idx + 1;
      if (nextIdx !== null) {
        e.preventDefault();
        const sibling = state?.siblingVisuals[nextIdx];
        navigate("/app/edit-visual", {
          replace: true,
          state: {
            ...state,
            imageUrl: sibling.imageUrl,
            allVersionUrls: sibling.allVersionUrls,
            renderId: sibling.renderId,
            parentRenderId: sibling.parentRenderId,
            rootRenderId: sibling.rootRenderId,
            currentSiblingIndex: nextIdx,
          },
        });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedId, isProcessing, state, navigate]);

  const [versions, setVersions] = useState<string[]>([]);
  // Parallel array of visual row ids matching `versions`. May contain undefined
  // entries if a URL came from navigation state without a known db id.
  const [versionIds, setVersionIds] = useState<(string | undefined)[]>([]);
  // Whether this visual has enough on it to be re-made. Read from the row,
  // not from router state, so Re-generate survives a refresh.
  const [canReGenerate, setCanReGenerate] = useState(false);

  // The price of the two things this page can spend. Read from the rate card
  // like every other surface — the editor used to write a hardcoded 20 onto
  // the row while the server charged 10, so the number a customer saw on the
  // asset and the number that left their balance disagreed.
  // Product reference photos, loaded from the visual's own product_id rather
  // than trusted to arrive in router state. State dies on refresh, and the
  // Asset Library never passed them at all — so "the product is wrong" edits
  // ran with no reference to correct against: Gemini saw only the flawed
  // image, never what the product should look like.
  const [dbProductImages, setDbProductImages] = useState<string[]>([]);

  const { costOf } = useGenerationCosts();
  const editCost = costOf("render_edit", FALLBACK_COSTS.render_edit);
  const reGenerateCost = costOf("studio_1k", FALLBACK_COSTS.render_base);
  const [searchParams] = useSearchParams();

  /**
   * Identity comes from the URL, not router state.
   *
   * location.state is discarded on refresh, so an editor that depended on it
   * bounced the user out the moment they reloaded or opened the URL directly —
   * and because that redirect used `replace`, the browser's own Back was left
   * pointing somewhere unexpected. With the visual id in the query string the
   * page rebuilds itself from the database, and Back has a real destination.
   *
   * State is still honoured when present: it lets the first paint happen
   * without waiting on a round trip.
   */
  const visualId = searchParams.get("v") || state?.renderId || null;
  const fromParam = searchParams.get("from");

  const [versionIndex, setVersionIndex] = useState(0);
  const activeImage = versions[versionIndex] || state?.imageUrl || "";
  const activeVisualId = versionIds[versionIndex] || state?.renderId;
  const isMaster = activeVisualId ? !!versionMasters[activeVisualId] : false;

  // Video generation for this master visual. Versions persist to
  // `visuals.videos` and are keyed to whichever version is on screen.
  const video = useVideoVersions({
    table: "visuals",
    rowId: activeVisualId,
    isOpen: true,
    imageUrl: activeImage,
    workspaceId: workspace?.id,
    userId: user?.id,
    formatRatio: "16:9",
    formatWidth: 1920,
    formatHeight: 1080,
  });
  const [versionModels, setVersionModels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (state?.allVersionUrls && state?.allVersionUrls.length > 0) {
      setVersions(state?.allVersionUrls);
      // We don't know the per-version ids yet — the DB-fallback effect below
      // will populate them.
      setVersionIds(state?.allVersionUrls.map(() => undefined));
      setVersionIndex(state?.allVersionUrls.length - 1);
    } else if (state?.imageUrl) {
      setVersions([state?.imageUrl]);
      setVersionIds([state?.renderId]);
      setVersionIndex(0);
    }
    // Reset annotations when switching siblings
    setAnnotations([]);
    setSelectedId(null);
    setSelectedIssues(new Set());
  }, [state?.imageUrl, state?.renderId]);

  // Always reconstruct the full version chain from the database so we have
  // authoritative per-version visual ids and is_master flags. This makes the
  // Approve toggle apply to the currently displayed version rather than to
  // whichever visual originally opened the page.
  useEffect(() => {
    if (!visualId) return;
    let cancelled = false;
    (async () => {
      const { data: current } = await supabase
        .from("visuals")
        .select("id, root_render_id, storage_path, is_master, brief_preset_id, product_id")
        .eq("id", visualId)
        .maybeSingle();
      if (!current || cancelled) return;
      // A preset or a product is enough for studio-generate to make another.
      setCanReGenerate(
        !!((current as any).brief_preset_id || (current as any).product_id),
      );
      const productId = (current as any).product_id;
      if (productId) {
        const { data: imgs } = await supabase
          .from("product_images")
          .select("storage_path")
          .eq("product_id", productId)
          .order("is_primary", { ascending: false })
          .order("created_at", { ascending: true })
          .limit(4);
        if (!cancelled && imgs) {
          setDbProductImages(imgs.map((img: any) =>
            supabase.storage.from("product-images").getPublicUrl(img.storage_path).data.publicUrl,
          ));
        }
      }
      const rootId = (current as any).root_render_id || (current as any).id;
      const { data: chain } = await supabase
        .from("visuals")
        .select("id, storage_path, created_at, is_master, model")
        .or(`id.eq.${rootId},root_render_id.eq.${rootId}`)
        .eq("status", "complete")
        .not("storage_path", "is", null)
        .order("created_at", { ascending: true });
      if (cancelled || !chain || chain.length === 0) return;

      const rows = chain as any[];
      const urls = rows
        .map((r) => supabase.storage.from("renders").getPublicUrl(r.storage_path).data.publicUrl);
      const ids = rows.map((r) => r.id as string);
      const masters: Record<string, boolean> = {};
      const models: Record<string, string> = {};
      for (const r of rows) {
        masters[r.id] = !!r.is_master;
        if (r.model) models[r.id] = r.model;
      }

      setVersions(urls);
      setVersionIds(ids);
      setVersionMasters(masters);
      setVersionModels(models);
      // Land on the version matching the current renderId (fallback: latest)
      const idx = rows.findIndex((r) => r.id === visualId);
      setVersionIndex(idx >= 0 ? idx : urls.length - 1);
    })();
    return () => { cancelled = true; };
  }, [visualId]);

  // Only redirect when there is nothing to work with at all. Previously this
  // fired whenever router state was missing — i.e. on every refresh — even
  // though the visual could have been loaded from its id.
  useEffect(() => {
    if (!visualId && !state?.imageUrl) {
      navigate("/app/asset-library", { replace: true });
    }
  }, [visualId, state?.imageUrl, navigate]);

  const toggleIssue = (key: PresetIssueKey) => {
    setSelectedIssues((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getNormalizedCoords = useCallback((e: React.MouseEvent) => {
    const rect = imgContainerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    return { x, y };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!drawMode || isProcessing || video.isPlaying) return;
    const coords = getNormalizedCoords(e);
    if (!coords) return;
    setIsDrawing(true);
    setDrawStart(coords);
    setCurrentRect({ x: coords.x, y: coords.y, w: 0, h: 0 });
  }, [drawMode, isProcessing, getNormalizedCoords]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDrawing || !drawStart) return;
    const coords = getNormalizedCoords(e);
    if (!coords) return;
    setCurrentRect({
      x: Math.min(drawStart.x, coords.x),
      y: Math.min(drawStart.y, coords.y),
      w: Math.abs(coords.x - drawStart.x),
      h: Math.abs(coords.y - drawStart.y),
    });
  }, [isDrawing, drawStart, getNormalizedCoords]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !currentRect) { setIsDrawing(false); return; }
    if (currentRect.w > 0.02 && currentRect.h > 0.02) {
      const newAnn: Annotation = {
        id: crypto.randomUUID(),
        x: currentRect.x, y: currentRect.y,
        width: currentRect.w, height: currentRect.h,
        comment: "",
      };
      setAnnotations((prev) => [...prev, newAnn]);
      setSelectedId(newAnn.id);
    }
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentRect(null);
  }, [isDrawing, currentRect]);

  const updateComment = (id: string, comment: string) => {
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, comment } : a)));
  };

  const deleteAnnotation = (id: string) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const [processingAnnotations, setProcessingAnnotations] = useState<Annotation[]>([]);
  const [isReRendering, setIsReRendering] = useState(false);
  const [isDeletingVersion, setIsDeletingVersion] = useState(false);

  /**
   * Return to wherever we came from. Falls back to browser history when no
   * return path was supplied, so an unknown or stale path can't strand the
   * user on a 404 — which is what a wrong returnTo used to do.
   */
  const goBack = () => {
    // `from` survives a refresh; router state does not. History is the last
    // resort, and only when it actually has an entry to go back to — after a
    // redirect that used `replace`, it may not.
    const target = fromParam || state?.returnTo;
    if (target && KNOWN_APP_ROUTES.some((r) => target.startsWith(r))) {
      navigate(target);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/app/asset-library");
  };

  const deleteVersion = async () => {
    if (isDeletingVersion) return;

    const removedId = versionIds[versionIndex];
    const urlToDelete = versions[versionIndex];

    // Format variants cascade with the visual. They used to vanish without
    // warning, so say how many are going before asking.
    const formatCount = removedId ? await countDeliverables([removedId]) : 0;
    const ok = await confirm({
      title: "Delete this version?",
      description: formatCount
        ? `Its ${formatCount} generated format${formatCount === 1 ? "" : "s"} will go too. This cannot be undone.`
        : "This cannot be undone.",
    });
    if (!ok) return;

    setIsDeletingVersion(true);
    try {
      // Resolve storage path: prefer the URL (works regardless of db row),
      // fall back to looking it up by id.
      let storagePath: string | null = null;
      try {
        const urlObj = new URL(urlToDelete);
        const pathMatch = urlObj.pathname.match(/\/renders\/(.+)$/);
        if (pathMatch) storagePath = decodeURIComponent(pathMatch[1]);
      } catch { /* not a parseable URL — ignore */ }

      // Prefer the id: the shared helper then also clears the format variants,
      // edit versions, videos, and every file behind them. Deleting by
      // storage_path is the fallback for a URL that arrived without a db id;
      // resolve it to an id first so that cleanup still happens.
      let targetId = removedId;
      if (!targetId && storagePath) {
        const { data: row } = await supabase
          .from("visuals")
          .select("id")
          .eq("storage_path", storagePath)
          .maybeSingle();
        targetId = (row as any)?.id;
      }

      if (targetId) {
        await deleteVisualsAndAssets([targetId]);
      } else if (storagePath) {
        // A file with no row behind it — drop the orphan.
        await supabase.storage.from("renders").remove([storagePath]);
      } else {
        // Neither an id nor a storage path: this version was never saved (a
        // raw data URL from the model). There is nothing to delete server
        // side, so just drop it locally — but say so, rather than reporting a
        // deletion that never happened.
        console.warn("Version had no saved row or file; dropping it locally only.");
      }

      // If this was the only version left there is nothing to show, so leave.
      // goBack honours ?from=, which router state does not survive a refresh
      // to provide.
      if (versions.length <= 1) {
        goBack();
        return;
      }

      // Update local state — drop this version and land on a sensible neighbor.
      setVersions((prev) => {
        const next = prev.filter((_, i) => i !== versionIndex);
        const newIndex = Math.min(versionIndex, next.length - 1);
        setVersionIndex(newIndex);
        return next;
      });
      setVersionIds((prev) => prev.filter((_, i) => i !== versionIndex));
      if (removedId) {
        setVersionMasters((prev) => {
          const next = { ...prev };
          delete next[removedId];
          return next;
        });
      }
      setAnnotations([]);
    } catch (err: any) {
      // Previously logged only, so a rejected delete was indistinguishable
      // from one that silently did nothing.
      console.error("Delete version error:", err);
      Sentry.captureException(err);
      toast.error(`Could not delete: ${err?.message || "unknown error"}`);
    } finally {
      setIsDeletingVersion(false);
    }
  };

  /**
   * Re-generate from the same starting point as the original.
   *
   * Reads the origin from the visual row rather than router state, so this
   * survives a refresh — `state` is gone after one, and Re-generate used to
   * disappear with it. It also means a Studio visual can re-generate, which
   * the brief-only version could not: the check was `state?.briefId`, and a
   * Studio visual has no brief.
   *
   * Everything needed now lives on the visual: `brief_preset_id` is the
   * preset, `brief_answers` is how it was answered, `product_id` is the
   * product. The brief row is no longer consulted, which is what lets briefs
   * stop being containers.
   *
   * The output is a fresh sibling, not a child of the current edit version.
   */
  const reRender = async () => {
    if (!user || !workspace || isReRendering) return;
    const sourceId = activeVisualId || state?.parentRenderId || state?.renderId;
    if (!sourceId) return;

    setIsReRendering(true);
    try {
      const { data: originRow } = await supabase
        .from("visuals")
        .select("brief_preset_id, brief_answers, product_id, session_id")
        .eq("id", sourceId)
        .maybeSingle();
      const originData = originRow as any;
      if (!originData) throw new Error("Could not load the visual to re-generate");

      // studio-generate needs a session to hang the turn on. Reuse the one
      // this visual came from; a brief-era visual has none, so make one.
      const sessionId: string | null =
        originData.session_id || (await createStudioSession(workspace.id, user.id));
      if (!sessionId) throw new Error("Could not open a Studio session");

      const answers = (originData.brief_answers as Record<string, string>) || {};
      const authHeader = await getAuthHeader();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 3 * 60 * 1000);

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/studio-generate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: authHeader },
          body: JSON.stringify({
            session_id: sessionId,
            workspace_id: workspace.id,
            productId: originData.product_id || undefined,
            briefConfigId: originData.brief_preset_id || undefined,
            briefAnswers: answers,
            cameraConfigId: answers._photographer_config_id || undefined,
            count: 1,
          }),
          signal: controller.signal,
        },
      ).finally(() => clearTimeout(timer));

      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(detail || `Generation failed (${res.status})`);
      }

      // Land in the session so the new visual appears where it was made.
      navigate(`/app/studio?session=${sessionId}`, { replace: true });
    } catch (error: any) {
      // Say what went wrong. This used to fail silently into a console line.
      console.error("Re-render error:", error);
      Sentry.captureException(error);
      toast.error(`Could not re-generate: ${error?.message || "unknown error"}`);
      setIsReRendering(false);
    }
  };

  const handleSubmit = async () => {
    const withComments = annotations.filter((a) => a.comment.trim());
    if (withComments.length === 0 && selectedIssues.size === 0) return;
    if (!user || !workspace) return;

    setEditError(null);
    setProcessingAnnotations(withComments);
    const savedAnnotations = [...withComments];
    const savedIssues = new Set(selectedIssues);
    setAnnotations([]);
    setSelectedId(null);
    setIsProcessing(true);

    // 2 min timeout so we never hang forever
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2 * 60 * 1000);

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/edit-render`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authHeader,
          },
          body: JSON.stringify({
            imageUrl: activeImage,
            annotations: savedAnnotations,
            selectedIssues: Array.from(savedIssues),
            productImageUrls: productRefUrls,
            workspace_id: workspace?.id,
          }),
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        let parsed: any = null;
        try { parsed = await response.json(); } catch { /* ignore */ }
        const rawMsg = parsed?.error || `Edit failed (${response.status})`;
        throw new Error(rawMsg);
      }

      const data = await response.json();
      const editedUrl = data.image;
      if (!editedUrl) {
        throw new Error("The model returned no image. Your request may have been blocked by safety filters — try rephrasing your comment.");
      }

      let finalUrl = editedUrl;
      let newEditId: string | undefined;
      try {
        // Persist every edit, whatever the visual came from. This used to be
        // gated on a briefId, so an edit of a Studio visual — which has none —
        // was never uploaded or inserted: it lived only as an in-memory data
        // URL, so it never reached the asset library and was gone as a version
        // the moment you navigated away.
        //
        // The origin is read from the parent row rather than router state, so
        // the new version inherits the right brief/session/product and this
        // works on a reloaded editor too.
        const parentId = activeVisualId || state?.parentRenderId || state?.renderId;
        const { data: parent } = parentId
          ? await supabase
              .from("visuals")
              .select("brief_id, brief_preset_id, session_id, source, product_id, root_render_id")
              .eq("id", parentId)
              .maybeSingle()
          : { data: null };
        const origin = parent as any;

        const editId = crypto.randomUUID();
        // Keep edits beside the images they derive from; `edits/` is the
        // fallback when a visual has neither origin.
        const folder = origin?.brief_id
          ? origin.brief_id
          : origin?.session_id
            ? `studio/${origin.session_id}`
            : "edits";
        const fileName = `${user.id}/${folder}/${editId}.png`;

        const base64Part = editedUrl.split(",")[1];
        let blob: Blob;
        if (base64Part) {
          const byteArray = Uint8Array.from(atob(base64Part), (c) => c.charCodeAt(0));
          blob = new Blob([byteArray], { type: "image/png" });
        } else {
          const imgResp = await fetch(editedUrl);
          blob = await imgResp.blob();
        }

        const { error: uploadErr } = await supabase.storage
          .from("renders")
          .upload(fileName, blob, { contentType: "image/png" });
        if (uploadErr) throw uploadErr;

        const rootId = origin?.root_render_id || parentId;

        const { data: insertedEdit, error: insertErr } = await supabase
          .from("visuals")
          .insert({
            brief_id: origin?.brief_id ?? state?.briefId ?? null,
            brief_preset_id: origin?.brief_preset_id ?? null,
            session_id: origin?.session_id ?? null,
            source: origin?.source ?? "free",
            product_id: origin?.product_id ?? null,
            workspace_id: workspace.id,
            user_id: user.id,
            storage_path: fileName,
            status: "complete",
            is_favorite: false,
            credits_charged: editCost,
            parent_render_id: parentId || null,
            root_render_id: rootId || null,
          } as any)
          .select()
          .single();
        if (insertErr) throw insertErr;

        const { data: storedUrl } = supabase.storage.from("renders").getPublicUrl(fileName);
        finalUrl = storedUrl.publicUrl;
        newEditId = (insertedEdit as any)?.id;
      } catch (storageErr) {
        // Still show the result, but say it was not saved — silently keeping a
        // data URL is what made a lost edit look like a successful one.
        console.error("Storage/DB save failed, using raw URL:", storageErr);
        Sentry.captureException(storageErr);
        setEditError("This version could not be saved, so it will disappear when you leave the page.");
      }

      setVersions((prev) => {
        const next = [...prev, finalUrl];
        setVersionIndex(next.length - 1);
        return next;
      });
      setVersionIds((prev) => [...prev, newEditId]);
      if (newEditId) {
        setVersionMasters((prev) => ({ ...prev, [newEditId]: false }));
      }
      setAnnotations([]);
      setSelectedId(null);
      setDrawMode(true);
      setSelectedIssues(new Set());
    } catch (error: any) {
      console.error("Edit error:", error);
      const aborted = error?.name === "AbortError";
      const raw = error?.message || "";
      let friendly: string;
      if (aborted) {
        friendly = "The edit timed out after 2 minutes. The model may be overloaded — please try again.";
      } else if (/safety|policy|blocked|prohibited|not allowed/i.test(raw)) {
        friendly = "Your request was blocked by the model's safety filters. Try rephrasing your comment or removing sensitive terms.";
      } else if (/no image|empty|null/i.test(raw)) {
        friendly = "The model returned no image. Your request may have been refused — try rephrasing your comment.";
      } else {
        friendly = raw || "Something went wrong while editing. Please try again.";
      }
      setEditError(friendly);
      // Restore annotations & selected issues so the user can adjust and retry
      setAnnotations(savedAnnotations);
      setSelectedIssues(savedIssues);
    } finally {
      clearTimeout(timer);
      setIsProcessing(false);
      setProcessingAnnotations([]);
    }
  };

  const annotationsWithComments = annotations.filter((a) => a.comment.trim());
  const canSubmit = annotations.length > 0 || selectedIssues.size > 0;
  // DB-loaded first: it survives refresh and works from every entry path.
  // Router state is the fallback for the moment before the row has loaded.
  const productRefUrls = dbProductImages.length > 0
    ? dbProductImages
    : (state?.productImageUrls || []);
  const hasProductImages = productRefUrls.length > 0;
  const hasAnyIssue = selectedIssues.size > 0;

  // `activeImage` comes from router state on the first paint and from the
  // database once the version chain loads, so this no longer blanks the page
  // on a refresh. Show a spinner while the DB round trip is in flight rather
  // than rendering nothing — a bare `null` here is what made a reloaded
  // editor look broken.
  if (!activeImage) {
    return (
      <div className="flex h-full min-h-[24rem] items-center justify-center">
        {visualId ? (
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-muted-foreground">This visual couldn't be loaded.</p>
            <Button variant="outline" size="sm" onClick={goBack}>
              Back
            </Button>
          </div>
        )}
      </div>
    );
  }

  const siblingIdx = state?.currentSiblingIndex ?? 0;
  const siblingCount = state?.siblingVisuals?.length ?? 0;
  const hasPrevSibling = !!state?.siblingVisuals && siblingIdx > 0;
  const hasNextSibling = !!state?.siblingVisuals && siblingIdx < siblingCount - 1;

  const goToSibling = (delta: number) => {
    if (!state?.siblingVisuals) return;
    const idx = siblingIdx + delta;
    if (idx < 0 || idx >= state?.siblingVisuals.length) return;
    const sibling = state?.siblingVisuals[idx];
    navigate("/app/edit-visual", {
      replace: true,
      state: {
        ...state,
        imageUrl: sibling.imageUrl,
        allVersionUrls: sibling.allVersionUrls,
        renderId: sibling.renderId,
        parentRenderId: sibling.parentRenderId,
        rootRenderId: sibling.rootRenderId,
        currentSiblingIndex: idx,
      },
    });
  };

  const handleToggleApprove = async () => {
    const targetId = activeVisualId;
    if (!targetId) return;
    const newVal = !isMaster;

    setVersionMasters((prev) => {
      const next = { ...prev };
      if (newVal) {
        for (const id of Object.keys(next)) next[id] = false;
      }
      next[targetId] = newVal;
      return next;
    });

    // Approval is a marker, not a slot. It used to un-approve every sibling in
    // the version tree — a "best of this batch" rule that made sense when the
    // asset library only showed approved images. Now that the library shows
    // everything and approval just means "we shipped this", several images
    // from one batch or session can legitimately be approved.
    await supabase.from("visuals").update({ is_master: newVal } as any).eq("id", targetId);
  };

  const imageActions = [
    {
      label: isMaster ? "Approved ✓" : "Approve",
      icon: <ShieldCheck className={cn("w-4 h-4", isMaster && "text-primary")} />,
      onClick: () => void handleToggleApprove(),
    },
    {
      label: "Download",
      icon: <Download className="w-4 h-4" />,
      onClick: () => {
        void downloadUrlAsFile(activeImage, `render-${Date.now()}.png`);
      },
    },
    // Re-generate re-runs the brief that produced this visual, so it is only
    // offered for brief-backed visuals. Studio visuals have no brief; showing
    // the action there would be a button that silently does nothing.
    ...(canReGenerate ? [{
      label: isReRendering ? "Generating…" : `Re-generate · ${reGenerateCost} credits`,
      icon: <RefreshCw className={cn("w-4 h-4", isReRendering && "animate-spin")} />,
      onClick: () => {
        if (!isReRendering) reRender();
      },
    }] : []),
    {
      label: versionIndex > 0 ? `Delete Edit ${versionIndex}` : "Delete",
      icon: <Trash2 className="w-4 h-4" />,
      disabled: isProcessing || isDeletingVersion,
      onClick: () => void deleteVersion(),
      destructive: true,
    },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] -m-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack} disabled={isProcessing}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back
          </Button>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold truncate leading-tight">
              {state?.sessionName || "Editing visual"}
            </span>
            {siblingCount > 0 && (
              <span className="text-xs text-muted-foreground tabular-nums leading-tight">
                Visual {siblingIdx + 1} of {siblingCount}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Image Canvas */}
        <div className="flex-1 flex flex-col overflow-auto bg-secondary/30">

          <div className="flex-1 flex items-start justify-center p-4" onClick={() => { if (selectedId && !drawMode) setSelectedId(null); }}>
            <div
              ref={imgContainerRef}
              data-tour="edit-visual.image"
              className={cn("group relative inline-block max-w-full select-none", drawMode && !isProcessing && "cursor-crosshair")}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => { if (isDrawing) handleMouseUp(); }}
            >
              <img src={activeImage} alt="Edit visual" className={cn("w-full object-contain rounded-lg", isReRendering && "opacity-40")} draggable={false} />
              <VideoPlayerOverlay video={video} formatWidth={1920} formatHeight={1080} />

              {/* Action menu overlay (top-right) */}
              <div
                data-tour="edit-visual.actions"
                className="absolute top-3 right-3 z-30 flex items-center gap-2"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <VideoToolbarControls video={video} className="bg-background/85 hover:bg-background backdrop-blur-sm h-9 text-xs shadow-md" />
                <ImageActionMenu
                  actions={imageActions}
                  info={activeVisualId && versionModels[activeVisualId] ? [{ label: "Model", value: versionModels[activeVisualId] }] : []}
                  className="bg-background/85 hover:bg-background backdrop-blur-sm h-9 w-9 shadow-md"
                />
              </div>

              {/* Version thumbnails overlay (left side, stacked) */}
              {versions.length > 1 && (
                <div
                  className="absolute top-3 left-3 z-30 flex flex-col gap-2 max-h-[calc(100%-1.5rem)] overflow-y-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {versions.map((vUrl, i) => (
                    <button
                      key={i}
                      onClick={() => { setVersionIndex(i); setAnnotations([]); }}
                      style={{ width: 150, borderRadius: 2 }}
                      className={cn(
                        "relative flex-shrink-0 aspect-video overflow-hidden border-2 transition-all",
                        versionIndex === i
                          ? "border-primary opacity-100"
                          : "border-white opacity-80 hover:opacity-100"
                      )}
                    >
                      <img src={vUrl} alt={i === 0 ? "Original" : `Edit ${i}`} className="w-full h-full object-cover bg-secondary" draggable={false} />
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-background/80 backdrop-blur-sm text-[10px] font-semibold leading-none text-foreground">
                        v{i + 1}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Carousel arrows */}
              {state?.siblingVisuals && state?.siblingVisuals.length > 1 && (
                <>
                  <button
                    type="button"
                    data-tour="edit-visual.carousel"
                    disabled={isProcessing || !hasPrevSibling}
                    aria-label="Previous visual"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); goToSibling(-1); }}
                    className={cn(
                      "absolute left-3 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-background/85 hover:bg-background backdrop-blur-sm shadow-md flex items-center justify-center transition-all",
                      (!hasPrevSibling || isProcessing) && "opacity-40 cursor-not-allowed"
                    )}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    disabled={isProcessing || !hasNextSibling}
                    aria-label="Next visual"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); goToSibling(1); }}
                    className={cn(
                      "absolute right-3 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-background/85 hover:bg-background backdrop-blur-sm shadow-md flex items-center justify-center transition-all",
                      (!hasNextSibling || isProcessing) && "opacity-40 cursor-not-allowed"
                    )}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Re-render loading overlay */}
              {isReRendering && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/60 rounded-lg z-20">
                  <Loader2 className="w-10 h-10 animate-spin text-primary mb-3" />
                  <span className="text-sm font-medium text-foreground">Generating new visual…</span>
                </div>
              )}

              {/* Annotations */}
              {!isProcessing && annotations.map((ann, i) => (
                <div
                  key={ann.id}
                  className={cn(
                    "absolute border-2 rounded transition-colors",
                    selectedId === ann.id ? "border-primary bg-primary/10" : "border-primary/60 bg-primary/5 hover:border-primary"
                  )}
                  style={{ left: `${ann.x * 100}%`, top: `${ann.y * 100}%`, width: `${ann.width * 100}%`, height: `${ann.height * 100}%` }}
                  onClick={(e) => { e.stopPropagation(); setSelectedId(ann.id); }}
                >
                  <div className="absolute -top-3 -left-3 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shadow-md">
                    {i + 1}
                  </div>
                </div>
              ))}

              {/* Sparkle effect */}
              {isProcessing && processingAnnotations.map((ann) => (
                <div
                  key={ann.id}
                  className="absolute rounded overflow-hidden pointer-events-none"
                  style={{ left: `${ann.x * 100}%`, top: `${ann.y * 100}%`, width: `${ann.width * 100}%`, height: `${ann.height * 100}%` }}
                >
                  {Array.from({ length: 20 }).map((_, si) => {
                    const size = [6, 8, 10, 12, 14, 7, 11, 9, 13, 8, 10, 6, 14, 12, 8, 10, 7, 11, 9, 13][si];
                    const cols = 5;
                    const rows = 4;
                    const col = si % cols;
                    const row = Math.floor(si / cols);
                    const cellW = 100 / cols;
                    const cellH = 100 / rows;
                    const jitterX = ((si * 17 + 7) % 11) / 11 * cellW * 0.6;
                    const jitterY = ((si * 13 + 3) % 11) / 11 * cellH * 0.6;
                    return (
                      <motion.div
                        key={si}
                        className="absolute"
                        style={{
                          left: `${col * cellW + cellW * 0.2 + jitterX}%`,
                          top: `${row * cellH + cellH * 0.2 + jitterY}%`,
                          width: size,
                          height: size,
                          filter: `drop-shadow(0 0 ${size / 2}px rgba(255,255,255,0.9))`,
                        }}
                        animate={{
                          opacity: [0, 1, 0.4, 1, 0],
                          scale: [0.3, 1.1, 0.7, 1.2, 0.3],
                          rotate: [0, 45, 0, -45, 0],
                        }}
                        transition={{
                          duration: 2.2,
                          repeat: Infinity,
                          delay: si * 0.18,
                          ease: "easeInOut",
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="white" className="w-full h-full">
                          <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41Z" />
                        </svg>
                      </motion.div>
                    );
                  })}
                </div>
              ))}

              {currentRect && (
                <div
                  className="absolute border-2 border-dashed border-primary bg-primary/10 rounded"
                  style={{ left: `${currentRect.x * 100}%`, top: `${currentRect.y * 100}%`, width: `${currentRect.w * 100}%`, height: `${currentRect.h * 100}%` }}
                />
              )}
            </div>
          </div>

          {/* Format Variants Panel.
              renderId must follow the version on screen and must not depend on
              router state — a URL-loaded editor has none, and the panel then
              reported every existing format as "Not generated". */}
          <FormatVariantsPanel
            sourceImageUrl={activeImage}
            productImageUrls={productRefUrls}
            renderId={activeVisualId}
            workspaceId={workspace?.id}
            userId={user?.id}
            productInfo={state?.productInfo}
          />
        </div>

        {/* Annotations Panel */}
        {annotations.length > 0 && (
          <div className="w-80 border-l border-border bg-card flex flex-col">
            <div className="p-3 border-b border-border">
              <h3 className="font-display font-semibold text-sm">Annotations ({annotations.length})</h3>
              <p className="text-xs text-muted-foreground mt-1">Describe what to change in each region</p>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              <AnimatePresence>
                {annotations.map((ann, i) => (
                  <motion.div
                    key={ann.id}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 20 }}
                    className={cn("rounded-lg border p-3 transition-colors", selectedId === ann.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}
                    onClick={() => setSelectedId(ann.id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">{i + 1}</div>
                        <span className="text-xs text-muted-foreground">Region {i + 1}</span>
                      </div>
                      <Button variant="ghost" size="icon" className="w-6 h-6" onClick={(e) => { e.stopPropagation(); deleteAnnotation(ann.id); }} disabled={isProcessing}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    <Textarea
                      placeholder="Describe what to change here..."
                      value={ann.comment}
                      onChange={(e) => updateComment(ann.id, e.target.value)}
                      className="min-h-[60px] text-xs resize-none"
                      disabled={isProcessing}
                      autoFocus={selectedId === ann.id && !ann.comment}
                    />
                    {selectedId === ann.id && (
                      <div className="mt-2">
                        <p className="text-[10px] text-muted-foreground mb-1.5">Quick suggestions:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {PRESET_ISSUES.map((issue) => (
                            <button
                              key={issue.key}
                              type="button"
                              disabled={isProcessing}
                              onClick={(e) => {
                                e.stopPropagation();
                                const current = ann.comment.trim();
                                const issueText = issue.label;
                                if (current.includes(issueText)) return;
                                const newComment = current ? `${current}. ${issueText}` : issueText;
                                updateComment(ann.id, newComment);
                                setSelectedIssues((prev) => {
                                  const next = new Set(prev);
                                  next.add(issue.key);
                                  return next;
                                });
                              }}
                              className={cn(
                                "px-2 py-1 rounded-md text-[10px] border transition-colors",
                                ann.comment.includes(issue.label)
                                  ? "bg-primary/15 border-primary/30 text-primary"
                                  : "bg-secondary border-transparent hover:border-primary/30 text-muted-foreground hover:text-foreground"
                              )}
                            >
                              {issue.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {hasAnyIssue && (
              <div className="px-3 py-2 border-t border-border">
                {hasProductImages ? (
                  <p className="text-xs text-primary flex items-center gap-1">
                    ✓ {productRefUrls.length} product ref image{productRefUrls.length > 1 ? "s" : ""} included
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">No product reference images available</p>
                )}
              </div>
            )}

            <div className="p-3 border-t border-border space-y-2">
              {editError && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-xs text-destructive flex items-start gap-2">
                  <span className="flex-1">{editError}</span>
                  <button
                    type="button"
                    className="text-destructive/70 hover:text-destructive"
                    onClick={() => setEditError(null)}
                    aria-label="Dismiss error"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <Button
                className="w-full gradient-primary text-primary-foreground"
                onClick={handleSubmit}
                disabled={isProcessing || !canSubmit}
              >
                {isProcessing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Apply edits · {editCost} credits
              </Button>
            </div>
          </div>
        )}
      </div>

      <VideoPromptDialog video={video} />
    </div>
  );
}
